/// <amd-module name="Agile/Directory/DirectoryContracts" />
import { ExceptionInfo } from "Agile/Contracts/Common";
import { IHubViewAwareState } from "VSS/Redux/Deprecated/HubViewState";
import { ITfsPageDataAwareState } from "Tfs/ReduxCommon/Modules/TfsPageData/TfsPageDataContracts";
import { INavigationAwareState } from "VSS/Redux/Modules/Navigation";

/** Struct which contains contextual information about the current directory page*/
export interface IDirectoryContext {
    /** Data provider for the initial data */
    myDataProviderId: string;
    /** Data provider for all of the data */
    allDataProviderId: string;
    /** The hub name to use for telemetry */
    hubName: string;
}

/** The full state for all Directory pages */
export interface IDirectoryViewState<T extends IDirectoryArtifact>
    extends IDirectoryAwareState<T>,
        IHubViewAwareState,
        INavigationAwareState,
        ITfsPageDataAwareState {}

export interface IDirectoryAwareState<T extends IDirectoryArtifact> {
    directoryState: IDirectoryState<T>;
}

/** The directory specific state */
export interface IDirectoryState<T extends IDirectoryArtifact> {
    /** Contains all the artifact state */
    directoryArtifactState: IDirectoryArtifactState<T>;
    /** Contains state related to favorites */
    favoriteState: IFavoriteState;
    /** Contains the rest of the state */
    uiState: IDirectoryUIState;
}

/** The artifact state for a directory page*/
export interface IDirectoryArtifactState<T extends IDirectoryArtifact> {
    /** All of the artifacts loaded at this time */
    artifacts: { [key: string]: T };
    /** The deleted artifacts (loaded from deleted favorites) */
    deletedArtifacts: { [key: string]: T };
    /** The ids of artifacts that the user is a member of */
    memberArtifactKeys: string[];
    /** The ids of recent artifacts */
    recentArtifactKeys: string[];
}

/** The favorite state for a directory page */
export interface IFavoriteState {
    /** All of the favorites loaded at this time*/
    favorites: { [key: string]: IFavoriteData };
    /** The status of a favorite */
    favoriteStatuses: { [key: string]: FavoriteStatus };
    /** Any errors associated with favorite actions */
    favoriteErrors: { [key: string]: Error };
}

/** "UI State" for directory pages */
export interface IDirectoryUIState {
    /** Is the filter bar visible */
    isFilterBarVisible: boolean;
    /** Is the all section loading */
    isAllSectionLoading: boolean;
    /** Has the all section data been populated */
    isAllSectionPopulated: boolean;
    /** Data provider exception data */
    exceptionInfo?: ExceptionInfo;
    /** The collapsed state for row groups */
    rowGroupCollapsedMap: { [key: string]: boolean };
}

// Model Contracts
export interface IDirectoryDataProviderData<T extends IDirectoryArtifact> {
    artifacts: T[];
    exceptionInfo?: ExceptionInfo;
}

export interface IMyDirectoryDataProviderData<T extends IDirectoryArtifact> extends IDirectoryDataProviderData<T> {
    memberKeys: string[];
    favorites: IFavoriteData[];
    recentKeys: string[];
}

/** Struct representing an identity that owns an artifact */
export interface IArtifactOwner {
    /** The identity id */
    id: string;
    /** The identity name */
    name: string;
    /** Is this identity a team? */
    isTeam: boolean;
}

/**
 * A base directory artifact
 * Those with additional artifact properties should extend this interface and use in generic components where appropriate
 */
export interface IDirectoryArtifact {
    /** A unique id for the artifact */
    id: string;
    /** The display name of the artifact */
    name: string;
    /** The owners of the artifact */
    owners: IArtifactOwner[];
    /** The url of the artifact */
    url: string;
    /** The favorite type associated with this artifact*/
    favoriteType: string;
}

export interface IFavoriteData {
    id: string;
    artifactId: string;
    artifactName: string;
    artifactType: string;
    artifactProperties: { [key: string]: string };
    isDeleted: boolean;
}

export const enum FavoriteStatus {
    Favorited = 1,
    Unfavorited = 2,
    Favoriting = 3,
    Unfavoriting = 4
}

// UI Contracts
export interface IDirectoryRowGroup<T extends IDirectoryArtifact> {
    alwaysVisible?: boolean;
    items: T[];
    groupKey: string;
    groupHeader: string;
    toggleable: boolean;
    collapsed?: boolean;
}
